import { useState, useEffect, useRef } from "react";
import {
  PlayIcon,
  DocumentIcon,
  ComputerDesktopIcon,
  StopIcon,
} from "@heroicons/react/24/outline";
import {
  Box,
  Tooltip,
  Stack,
  Typography,
  Chip,
  DialogContentText,
} from "@mui/material";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { useNavigate, Link } from "react-router-dom"

import { formatDurationInSeconds } from "../../utils/commonfunctions";
import CustomChip from "../chips/CustomChip";
import ChipComponent from "../chips/ChipComponent/ChipComponent";
import { getExecutionResultStatusVariant, getStatusIcon } from "../chips/ChipComponent/chipVariants";
import TableComponent from "../TableComponent";
import TestLaunchPrimaryCard from "./TestLaunchPrimaryCard";
import { statusChipStyles, formatStatusKey, statusOptionsExecutionResult } from "./statusColorPallete";
import { reExecuteTestcase, abortExecution } from "../../utils/api/testlaunch_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import RerunExecutionConfirmModal from "./RerunExecutionConfirmModal";
import ConfirmationModal from "../Modal/ConfirmationModal";
import { useSnackbar } from "../../utils/useSnackbar";
import DEFAULT_ROUTES from "../../constants/routes";
import { TestLaunchStatus } from "../../enums/TestLaunchEnums";

const ExecutionResultAutomatedTable = ({
  componentData,
  componentDataDispatch,
  onCLickViewDetails,
  refetch,
  silentRefetch,
  projectId
}) => {
  const navigate = useNavigate();

  const initialRerunRowData = {
    isModalOpen: false,
    data: null,
  };
  const [rerunRowData, setRerunRowData] = useState(initialRerunRowData);
  
  const initialAbortRowData = {
    isModalOpen: false,
    data: null,
  };
  const [abortRowData, setAbortRowData] = useState(initialAbortRowData);
  const onChangeFilters = (model) => {
    componentDataDispatch({
      type: "SET_FILTERS",
      payload: model,
    });
  };

  const handleCustomPaginationPageChange = (event, page) => {
    componentDataDispatch({
      type: "SET_PAGINATION_MODEL",
      payload: { page },
    });
  };
  const handleCustomPaginationPageSizeChange = (pageSize) => {
    componentDataDispatch({
      type: "SET_PAGINATION_MODEL",
      payload: { page: 0, pageSize },
    });
  };
  const columns = [
    {
      field: "orderIndex",
      headerName: "#",
      minWidth: 40,
      maxWidth: 60,
      flex: 0.3,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const rowIndex = (componentData?.rows || []).findIndex((r) => r.id === params.row.id);
        const { page = 0, pageSize = 25 } = componentData?.paginationModel || {};
        return rowIndex >= 0 ? page * pageSize + rowIndex + 1 : '';
      },
    },
    {
      field: "id",
      headerName: "Test Case",
      // minWidth: 300,
      // maxWidth: 400,
      disableColumnMenu: true,
      sortable: false,
      flex: 2,
      renderCell: (params, data) => {
        const { row: { testCaseKey, testCaseTitle, labels = [], testCaseId, moduleId } = {} } =
          params;

        let testCaseUrl = "#";

        if (testCaseId && moduleId && projectId) {
          const searchParams = new URLSearchParams({
            moduleId,
            testCaseId,
          });
          testCaseUrl = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${projectId}${DEFAULT_ROUTES.TEST_CASES}?${searchParams.toString()}`;
        }
        if(!testCaseId) return null
        return (
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <ComputerDesktopIcon
                className="icon"
                style={{ color: "#2563eb" }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between", // spaces the 3 divs apart
              }}
            >
              {testCaseKey && (
                <Box>
                  <Typography fontWeight="bold"
                    sx={{
                      cursor: 'pointer',
                      color: "#2563eb",
                      fontSize: { xl: "14px", md: "12px" }

                    }}

                    component={Link}
                    to={testCaseUrl}
                  >
                    {testCaseKey}
                  </Typography>
                </Box>
              )}
              {testCaseTitle && (
                <Box>
                  <Typography variant="caption">{testCaseTitle}</Typography>
                </Box>
              )}
              {labels && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {labels.map(({ name }) => {
                    return (
                      <Chip
                        key={name}
                        label={`#${name}`}
                        color="primary"
                        size="small"
                        sx={{
                          backgroundColor: "#DBEAFE", // blue-100
                          color: "#2563EB", // blue-600
                          fontWeight: 500,
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      type: 'singleSelect',
      valueOptions: statusOptionsExecutionResult,
      sortable: false,
      // minWidth: 150,
      // maxWidth: 240,
      flex: 0.6,
      renderCell: (params) => {
        const status = formatStatusKey(params.value);
        const isPending = params.value === 'pending';
        const isInProgress = params.value === 'in_progress';
        return (
          <ChipComponent
            variant={getExecutionResultStatusVariant(params.value)}
            icon={getStatusIcon(params.value)}
            size="small"
            loading={isPending || isInProgress}
          >
            {status}
          </ChipComponent>
        );
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      // minWidth: 140,
      // maxWidth: 240,
      disableColumnMenu: true,
      sortable: false,
      flex: 0.6,
      renderCell: (params) => formatDurationInSeconds(params.value),
    },
    {
      field: "executionStartTime",
      headerName: "Execution At",
      // minWidth: 180,
      // maxWidth: 290,
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const formattedDate = moment(params.value).format(
          "MMM DD, YYYY hh:mm A"
        );

        return <span>{formattedDate}</span>;
      },
    },
    {
      headerName: "Actions",
      field: "Actions",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const hasViewButton = ["failed", "passed"].includes(params.row.status);
        const hasRerunButton = isExecutable && ["failed", "passed"].includes(params.row.status);
        const hasAbortButton = isExecutable && ["pending", "in_progress"].includes(params.row.status);
        const sessionId = params.row.sessionId;
        const hasAnyAction = hasViewButton || hasRerunButton || hasAbortButton || sessionId;

        return (
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            {hasViewButton && (
              <Tooltip title="View">
                <DocumentIcon
                  className="icon"
                  style={{
                    color: "#2563eb",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    onCLickViewDetails(params);
                  }}
                />
              </Tooltip>
            )}
            {hasRerunButton && (
              <Tooltip title="Rerun">
                <PlayIcon
                  className="icon"
                  style={{
                    color: "#16a34a",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setRerunRowData({
                      isModalOpen: true,
                      data: params.row,
                    });
                  }}
                />
              </Tooltip>
            )}
            {hasAbortButton && (
              <Tooltip title="Abort">
                <StopIcon
                  className="icon"
                  style={{
                    color: "#dc2626",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setAbortRowData({
                      isModalOpen: true,
                      data: params.row,
                    });
                  }}
                />
              </Tooltip>
            )}
            {sessionId && (
              <Tooltip title={`Session ID: ${sessionId}`}>
                <ClipboardDocumentIcon
                  className="icon"
                  style={{ color: "#64748b", cursor: "pointer" }}
                  onClick={() => navigator.clipboard.writeText(sessionId)}
                />
              </Tooltip>
            )}
            {!hasAnyAction && (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Stack>
        );
      },
    },
  ];

  const { summaryReport } = componentData;
  const isExecutable = ![TestLaunchStatus.COMPLETED, TestLaunchStatus.ABORTED].includes(summaryReport?.status);
  const { counts: { totalNLPCount = 0, passedNLPCount = 0 } = {} } =
    summaryReport || {};
  const totalPassPercentage = totalNLPCount
    ? Number((passedNLPCount / totalNLPCount) * 100).toFixed(2)
    : 0;

  // Polling interval ref
  const pollingIntervalRef = useRef(null);
  const POLLING_INTERVAL = 15000; // 15 seconds

  // Set up polling for automated test results
  useEffect(() => {
    // Only poll if:
    // 1. Launch is in progress (not completed/aborted)
    // 2. silentRefetch function is available (for background updates)
    // 3. Document is visible (tab is active)
    const launchStatus = summaryReport?.status;
    const isLaunchInProgress = 
      launchStatus !== TestLaunchStatus.COMPLETED &&
      launchStatus !== TestLaunchStatus.ABORTED &&
      isExecutable;

    // Use silentRefetch if available, otherwise fall back to refetch
    const refreshFunction = silentRefetch || refetch;
    const shouldPoll = isLaunchInProgress && refreshFunction;

    if (shouldPoll) {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Function to check if document is visible and poll silently
      const pollData = () => {
        // Only poll if document is visible (tab is active)
        if (document.visibilityState === 'visible' && refreshFunction) {
          refreshFunction();
        }
      };

      // Set up polling interval
      pollingIntervalRef.current = setInterval(pollData, POLLING_INTERVAL);
    } else {
      // Stop polling if conditions are not met
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isExecutable, refetch, silentRefetch, summaryReport?.status]);

  const fields = [
    {
      Component: DialogContentText,
      name: "info",
      label: "Info",
      children: (
        <>
          <strong>Warning:</strong> The previous data for the result will be
          lost and re-updated.
          <br />
          This action cannot be undone. Please confirm if you want to proceed.
        </>
      ),
    },
  ];
  
  const { showSnackbar } = useSnackbar();

  const onClose = () => {
    setRerunRowData(initialRerunRowData);
  };
  
  const onAbortClose = () => {
    setAbortRowData(initialAbortRowData);
  };
  
  const onClickRerunConfirmation = async () => {
    const response = await reExecuteTestcase({
      executionResultId: rerunRowData?.data?.id,
    });

    if (response.status === STATUS.SUCCESS) {
      onClose();
      silentRefetch();
      showSnackbar(response.message || "Testcase is re-executing !!!");
    } else {
      showSnackbar(response.error || "Failed to execute testcase !!!","error");
    }
  };
  
  const onClickAbortConfirmation = async () => {
    const response = await abortExecution({
      executionResultId: abortRowData?.data?.id,
    });

    if (response.status === STATUS.SUCCESS) {
      onAbortClose();
      refetch();
      showSnackbar(response.message || "Test case execution aborted successfully!");
    } else {
      showSnackbar(response.error || "Failed to abort execution", "error");
    }
  };
  
  const abortConfirmationMessage = (
    <>
      Are you sure you want to abort this test case execution?
      <br />
      <br />
      This will stop the execution of this test case immediately. Other test cases in the launch will continue running.
      <br />
      <br />
      This action cannot be undone.
    </>
  );

  return (
    <>
      <RerunExecutionConfirmModal
        onClose={onClose}
        title={`Re-run Test Case`}
        onClickRerun={onClickRerunConfirmation}
        fields={fields}
        data={rerunRowData}
      />
      <ConfirmationModal
        open={abortRowData?.isModalOpen}
        onClose={onAbortClose}
        onConfirm={onClickAbortConfirmation}
        title="Abort Test Case Execution"
        message={abortConfirmationMessage}
      />
      <TestLaunchPrimaryCard
        primaryLabel={`Automated Tests Execution`}
        secondaryLabel={`${passedNLPCount} of ${totalNLPCount} tests completed`}
        percentage={totalPassPercentage}
      />
      <TableComponent
        viewType="testExecutionResult"
        title=" "
        headerActionButtons={[]}
        columns={columns}
        rows={componentData?.rows}
        isCheckbox={false}
        isRowSelection={true}
        hideColumnsVisibilityButton={true}
        idField="id"
        isPagination={true}
        rowCount={componentData?.total?.automated}
        paginationMode="server"
        paginationModel={componentData?.paginationModel}
        handleCustomPaginationPageChange={handleCustomPaginationPageChange}
        handleCustomPaginationPageSizeChange={
          handleCustomPaginationPageSizeChange
        }
        isCustomPagination={true}
        customStyles={{
          "& .MuiDataGrid-cell": {
            py: 0.5,
          },
        }}
        columnHeaderStyle={{
          fontWeight: 600,
          fontSize: "1rem",
          color: "#0f172a",
          whiteSpace: "normal",
          textAlign: "left",
          padding: 0,
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          maxHeight: "3em",
        }}
        getRowHeight={() => "auto"}
        isHeaderContentVisible={false}
        loading={componentData?.isLoadingFetchTabularData}
        isColumnResizable={!componentData?.isLoadingFetchTabularData}
        tableHeight={'calc(100vh - 300px)'}
        filterMode="server"
        handleFilterModelChange={onChangeFilters}
        
      />
    </>
  );
};

export default ExecutionResultAutomatedTable;
